/* eslint @typescript-eslint/no-explicit-any: 0 */
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { AttributesData } from 'types';

import * as cognito from 'lib/cognito';
import { priceTypeStore } from 'lib/priceTypeStore';
import { tokenStore } from 'lib/tokenStore';

import { usersDataService } from 'services';

import { routes } from 'data/routes';

export enum AuthStatus {
  Loading,
  SignedIn,
  SignedOut,
}

export interface IAuth {
  authStatus?: AuthStatus;
  getSession?: any;
  signInWithEmail?: any;
  signOut?: any;
  completeNewPasswordChallenge?: any;
  isNewPassword?: boolean;
  isNewPasswordSet?: any;
  forgotPassword?: any;
  confirmPassword?: any;
}

type AuthProps = {
  children: React.ReactNode;
};

const defaultState: IAuth = {
  authStatus: AuthStatus.Loading,
};

export const AuthContext = createContext(defaultState);

export function AuthIsSignedIn(props: AuthProps): React.ReactElement {
  const { children } = props;
  const { authStatus }: IAuth = useContext(AuthContext);

  // eslint-disable-next-line
  return <>{authStatus === AuthStatus.SignedIn ? children : null}</>;
}

export function AuthIsNotSignedIn(props: AuthProps): React.ReactElement {
  const { children } = props;
  const { authStatus }: IAuth = useContext(AuthContext);

  // eslint-disable-next-line
  return <>{authStatus === AuthStatus.SignedOut ? children : null}</>;
}

export const AuthProvider: React.FunctionComponent<AuthProps> = ({ children }) => {
  const [authStatus, setAuthStatus] = useState(AuthStatus.Loading);
  const [isNewPassword, isNewPasswordSet] = useState(false);
  const navigate = useNavigate();

  async function signInWithEmail(username: string, password: string) {
    try {
      await cognito.signInWithEmail(username, password, () => navigate(routes.newPassword));
      const res = await usersDataService.getCurrentUser();
      if (res.data.permissions.includes('CATALOGUE')) {
        setAuthStatus(AuthStatus.SignedIn);
      } else {
        toast.error('Account permissions are missing!');
      }
    } catch (err) {
      setAuthStatus(AuthStatus.SignedOut);
      throw err;
    }
  }

  async function getSession() {
    const session = await cognito.getSession();

    return session;
  }

  async function completeNewPasswordChallenge(password: string, attributes: AttributesData) {
    cognito
      .completeNewPasswordChallenge(password, attributes)
      .then(() => {
        isNewPasswordSet(true);
        navigate(routes.login);
      })
      .catch((err) => {
        throw new Error(err);
      });
  }

  async function forgotPassword(email: string, callback: () => void) {
    await cognito.forgotPassword(email, callback);
  }

  function confirmPassword(email: string, code: string, password: string) {
    return cognito.confirmPassword(email, code, password);
  }

  function signOut() {
    cognito.signOut();
    tokenStore.clearTokens();
    priceTypeStore.clearPriceType();

    setAuthStatus(AuthStatus.SignedOut);
  }

  const state: IAuth = useMemo(
    () => ({
      getSession,
      authStatus,
      signInWithEmail,
      signOut,
      completeNewPasswordChallenge,
      isNewPassword,
      isNewPasswordSet,
      forgotPassword,
      confirmPassword,
    }),
    [authStatus, isNewPassword]
  );

  useEffect(() => {
    let cancelled = false;

    async function checkSessionInfo() {
      try {
        const session: any = await getSession();
        const res = await usersDataService.getCurrentUser();

        if (cancelled) return;

        if (res.data.permissions.includes('CATALOGUE')) {
          tokenStore.setTokens(
            session.accessToken.jwtToken,
            session.refreshToken.token,
            session.idToken.jwtToken
          );

          setAuthStatus(AuthStatus.SignedIn);
        }
      } catch (err) {
        if (!cancelled) {
          if (tokenStore.hasToken()) {
            signOut();
            toast.error('Session expired. Please sign in again.', {
              id: 'session-expired',
              duration: 5000,
            });
            setTimeout(() => {
              window.location.href = '/login';
            }, 1500);
          } else {
            setAuthStatus(AuthStatus.SignedOut);
          }
        }
      }
    }

    const sessionInterval = setInterval(checkSessionInfo, 1700000);

    checkSessionInfo();

    return () => {
      cancelled = true;
      clearInterval(sessionInterval);
    };
  }, []);

  if (authStatus === AuthStatus.Loading) {
    return null;
  }

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
};
